<template>
  <b-modal
    v-model="showModal"
    :title="editId ? 'Edit Lead' : 'Add Lead'"
    size="lg"
    class="m-4"
    no-close-on-backdrop
    @hidden="$emit('modal-closed')"
    @ok="submitHandler"
  >
    <b-form @submit.prevent="submitHandler">
      <b-row>
        <b-col cols="12">
          <b-alert
            variant="danger"
            :show="errorMessage ? true : false"
          >
            <div
              v-if="typeof errorMessage === 'string'"
              class="alert-body"
            >
              <p>{{ errorMessage }}</p>
            </div>
            <div
              v-else-if="errorMessage.detail"
              class="alert-body"
            >
              <p>{{ errorMessage.detail }}</p>
            </div>
            <div
              v-else-if="errorMessage.non_field_errors"
              class="alert-body"
            >
              <p>{{ errorMessage.non_field_errors[0] }}</p>
            </div>
            <div
              v-else-if="errorMessage.message"
              class="alert-body"
            >
              <p>{{ errorMessage.message }}</p>
            </div>
          </b-alert>
        </b-col>

        <b-col cols="6">
          <b-form-group
            label="Party Name (New Lead) *"
            label-for="partyName"
          >
            <b-form-input
              id="partyName"
              v-model="partyName"
              type="text"
              placeholder="Enter Party Name"
            />
            <p
              v-if="errorMessage && errorMessage.party_name"
              class="error-message"
            >
              {{ errorMessage.party_name[0] }}
            </p>
          </b-form-group>
        </b-col>

        <b-col cols="6">
          <b-form-group
            label="Area *"
            label-for="area"
          >
            <b-form-input
              id="area"
              v-model="area"
              type="text"
              placeholder="Enter Area"
            />
            <p
              v-if="errorMessage && errorMessage.area"
              class="error-message"
            >
              {{ errorMessage.area[0] }}
            </p>
          </b-form-group>
        </b-col>

        <b-col cols="6">
          <b-form-group label="Owner Mobile Numbers">
            <div
              v-for="(mobile, index) in ownerMobiles"
              :key="`owner-${index}`"
              class="d-flex align-items-center mb-50"
            >
              <b-form-input
                v-model="ownerMobiles[index]"
                type="text"
                placeholder="Enter Owner Mobile"
                @input="onMobileInput('ownerMobiles', index, $event)"
              />
              <feather-icon
                v-if="ownerMobiles.length > 1"
                v-b-tooltip.hover="{boundary:'window'}"
                title="Delete"
                icon="TrashIcon"
                size="18"
                class="cursor-pointer text-danger ml-50"
                @click="removeOwnerMobile(index)"
              />
              <feather-icon
                v-if="index === ownerMobiles.length - 1"
                v-b-tooltip.hover="{boundary:'window'}"
                title="Add More"
                icon="PlusCircleIcon"
                size="20"
                class="cursor-pointer text-primary ml-50"
                @click="addOwnerMobile"
              />
            </div>
            <p
              v-if="errorMessage && errorMessage.owner_mobile"
              class="error-message"
            >
              {{ formatArrayError(errorMessage.owner_mobile) }}
            </p>
          </b-form-group>
        </b-col>

        <b-col cols="6">
          <b-form-group label="Manager Mobile Numbers">
            <div
              v-for="(mobile, index) in managerMobiles"
              :key="`manager-${index}`"
              class="d-flex align-items-center mb-50"
            >
              <b-form-input
                v-model="managerMobiles[index]"
                type="text"
                placeholder="Enter Manager Mobile"
                @input="onMobileInput('managerMobiles', index, $event)"
              />
              <feather-icon
                v-if="managerMobiles.length > 1"
                v-b-tooltip.hover="{boundary:'window'}"
                title="Delete"
                icon="TrashIcon"
                size="18"
                class="cursor-pointer text-danger ml-50"
                @click="removeManagerMobile(index)"
              />
              <feather-icon
                v-if="index === managerMobiles.length - 1"
                v-b-tooltip.hover="{boundary:'window'}"
                title="Add More"
                icon="PlusCircleIcon"
                size="20"
                class="cursor-pointer text-primary ml-50"
                @click="addManagerMobile"
              />
            </div>
            <p
              v-if="errorMessage && errorMessage.manager_mobile"
              class="error-message"
            >
              {{ formatArrayError(errorMessage.manager_mobile) }}
            </p>
          </b-form-group>
        </b-col>

        <b-col cols="12">
          <b-form-group label="Emails">
            <b-row>
              <b-col
                v-for="(email, index) in emails"
                :key="`email-${index}`"
                cols="6"
                class="mb-50"
              >
                <div class="d-flex align-items-center">
                  <b-form-input
                    v-model="emails[index]"
                    type="email"
                    placeholder="Enter Email"
                  />
                  <feather-icon
                    v-if="emails.length > 1"
                    v-b-tooltip.hover="{boundary:'window'}"
                    title="Delete"
                    icon="TrashIcon"
                    size="18"
                    class="cursor-pointer text-danger ml-50"
                    @click="removeEmail(index)"
                  />
                  <feather-icon
                    v-if="index === emails.length - 1"
                    v-b-tooltip.hover="{boundary:'window'}"
                    title="Add More"
                    icon="PlusCircleIcon"
                    size="20"
                    class="cursor-pointer text-primary ml-50"
                    @click="addEmail"
                  />
                </div>
              </b-col>
            </b-row>
            <p
              v-if="errorMessage && errorMessage.emails"
              class="error-message"
            >
              {{ formatArrayError(errorMessage.emails) }}
            </p>
          </b-form-group>
        </b-col>

        <b-col cols="6">
          <b-form-group
            label="Drug License"
            label-for="drugLicense"
          >
            <b-form-input
              id="drugLicense"
              v-model="drugLicense"
              type="text"
              placeholder="Enter Drug License"
            />
            <p
              v-if="errorMessage && errorMessage.drug_license"
              class="error-message"
            >
              {{ errorMessage.drug_license[0] }}
            </p>
          </b-form-group>
        </b-col>

        <b-col cols="6">
          <label for="drug-license-validity">Drug License Validity</label>
          <b-form-datepicker
            id="drug-license-validity"
            v-model="drugLicenseValidity"
            menu-class="w-100"
            calendar-width="100%"
          />
          <p
            v-if="errorMessage && errorMessage.drug_license_validity"
            class="error-message"
          >
            {{ errorMessage.drug_license_validity[0] }}
          </p>
        </b-col>

        <b-col cols="6">
          <div class="d-flex align-items-center mb-1">
            <label class="mb-0 mr-1">GST Applicable</label>
            <b-form-checkbox
              v-model="gstApplicable"
              switch
              size="lg"
            />
          </div>
          <p
            v-if="errorMessage && errorMessage.gst_applicable"
            class="error-message"
          >
            {{ errorMessage.gst_applicable[0] }}
          </p>
          <b-form-group
            v-if="gstApplicable"
            label="GST Number"
            label-for="gstNumber"
          >
            <b-form-input
              id="gstNumber"
              v-model="gstNumber"
              type="text"
              placeholder="Enter GST Number"
            />
            <p
              v-if="errorMessage && errorMessage.gst_number"
              class="error-message"
            >
              {{ errorMessage.gst_number[0] }}
            </p>
          </b-form-group>
          <div class="d-flex align-items-center">
            <label class="mb-0 mr-1">Retail App Installed</label>
            <b-form-checkbox
              v-model="retailAppInstalled"
              switch
              size="lg"
            />
          </div>
          <p
            v-if="errorMessage && errorMessage.retail_app_installed"
            class="error-message"
          >
            {{ errorMessage.retail_app_installed[0] }}
          </p>
        </b-col>

        <b-col cols="6">
          <b-form-group
            label="Expected Monthly Turnover"
            label-for="expectedMonthlyTurnover"
          >
            <b-form-input
              id="expectedMonthlyTurnover"
              v-model="expectedMonthlyTurnover"
              type="number"
              placeholder="Enter Expected Monthly Turnover"
            />
            <p
              v-if="errorMessage && errorMessage.expected_monthly_turnover"
              class="error-message"
            >
              {{ errorMessage.expected_monthly_turnover[0] }}
            </p>
          </b-form-group>
        </b-col>
        <b-col cols="6">
          <b-form-group
            label="Discount (%)"
            label-for="discount"
          >
            <b-form-input
              id="discount"
              v-model="discount"
              type="number"
              step="0.01"
              placeholder="Enter Discount"
            />
            <p
              v-if="errorMessage && errorMessage.discount"
              class="error-message"
            >
              {{ errorMessage.discount[0] }}
            </p>
          </b-form-group>
        </b-col>

        <b-col cols="6">
          <b-form-group label="Credit Type">
            <custom-v-select
              v-model="creditType"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="creditTypeOptions"
              label="text"
              :reduce="opt => opt.value"
              placeholder="Select Credit Type"
              @input="onCreditTypeChange"
            />
            <p
              v-if="errorMessage && errorMessage.credit_type"
              class="error-message"
            >
              {{ errorMessage.credit_type[0] }}
            </p>
          </b-form-group>
        </b-col>

        <b-col
          v-if="creditType === 'bill_to_bill'"
          cols="6"
        >
          <b-form-group
            label="Credit Days"
            label-for="creditDays"
          >
            <b-form-input
              id="creditDays"
              v-model="creditDays"
              type="number"
              placeholder="Enter Credit Days"
            />
            <p
              v-if="errorMessage && errorMessage.credit_days"
              class="error-message"
            >
              {{ errorMessage.credit_days[0] }}
            </p>
          </b-form-group>
        </b-col>

        <b-col
          v-if="creditType === 'statement'"
          cols="6"
        >
          <b-form-group label="Statement Duration">
            <custom-v-select
              v-model="statementDuration"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="statementDurationOptions"
              label="text"
              :reduce="opt => opt.value"
              placeholder="Select Statement Duration"
            />
            <p
              v-if="errorMessage && errorMessage.statement_duration"
              class="error-message"
            >
              {{ errorMessage.statement_duration[0] }}
            </p>
          </b-form-group>
        </b-col>

        <b-col cols="6">
          <b-form-group label="Transport Type">
            <custom-v-select
              v-model="transportType"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="transportTypeOptions"
              label="text"
              :reduce="opt => opt.value"
              placeholder="Select Transport Type"
              @input="onTransportTypeChange"
            />
            <p
              v-if="errorMessage && errorMessage.transport_type"
              class="error-message"
            >
              {{ errorMessage.transport_type[0] }}
            </p>
          </b-form-group>
        </b-col>

        <b-col
          v-if="transportType === 'bus'"
          cols="6"
        >
          <b-form-group
            label="Bus Name"
            label-for="busName"
          >
            <b-form-input
              id="busName"
              v-model="busName"
              type="text"
              placeholder="Enter Bus Name"
            />
            <p
              v-if="errorMessage && errorMessage.bus_name"
              class="error-message"
            >
              {{ errorMessage.bus_name[0] }}
            </p>
          </b-form-group>
        </b-col>

        <b-col
          v-if="transportType === 'bus'"
          cols="6"
        >
          <label for="bus-timings">Bus Timings</label>
          <b-form-timepicker
            id="bus-timings"
            v-model="busTimings"
            placeholder="Select Bus Timing"
          />
          <p
            v-if="errorMessage && errorMessage.bus_timings"
            class="error-message"
          >
            {{ errorMessage.bus_timings[0] }}
          </p>
        </b-col>

        <b-col cols="6">
          <b-form-group label="Status">
            <custom-v-select
              v-model="status"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="statusOptions"
              label="text"
              :reduce="opt => opt.value"
              placeholder="Select Status"
            />
            <p
              v-if="errorMessage && errorMessage.status"
              class="error-message"
            >
              {{ errorMessage.status[0] }}
            </p>
          </b-form-group>
        </b-col>

        <b-col cols="12">
          <b-form-group label="Drug License Image">
            <b-form-file
              v-model="drugLicenseImage"
              placeholder="Choose file or drop it here..."
              drop-placeholder="Drop file here..."
              accept="image/*"
            />
            <div
              v-if="existingDrugLicenseImage && !drugLicenseImage"
              class="mt-1"
            >
              <small class="text-muted">Existing image:</small>
              <div class="mt-50">
                <a
                  :href="existingDrugLicenseImage"
                  target="_blank"
                >
                  <feather-icon
                    v-b-tooltip.hover="{boundary:'window'}"
                    title="View Image"
                    icon="ImageIcon"
                    size="20"
                    class="cursor-pointer text-primary"
                  />
                  <span class="ml-50">View Existing Image</span>
                </a>
              </div>
            </div>
            <p
              v-if="errorMessage && errorMessage.drug_license_image"
              class="error-message"
            >
              {{ errorMessage.drug_license_image[0] }}
            </p>
          </b-form-group>
        </b-col>
      </b-row>
    </b-form>
    <template #modal-footer="{ ok, cancel }">
      <b-button
        variant="secondary"
        @click="cancel()"
      >
        Cancel
      </b-button>
      <b-button
        variant="primary"
        :disabled="saving"
        @click="ok()"
      >
        {{ editId ? 'Update' : 'Create' }}
        <b-spinner
          v-if="saving"
          small
          label="Small Spinner"
        />
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import {
  BModal, BRow, BCol, BFormGroup, BButton, BFormInput, BFormDatepicker, BFormTimepicker, BForm, BSpinner, BAlert, BFormFile, BFormCheckbox, VBTooltip,
} from 'bootstrap-vue'
import CustomVSelect from '@/components/UI/CustomVSelect.vue'
import axios from 'axios'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  name: 'AddLead',
  directives: {
    'b-tooltip': VBTooltip,
  },
  components: {
    CustomVSelect,
    BModal,
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    BForm,
    BFormDatepicker,
    BFormTimepicker,
    BButton,
    BSpinner,
    BAlert,
    BFormFile,
    BFormCheckbox,
  },
  props: {
    editId: {
      type: [Number, String, null],
      default: null,
    },
  },
  data() {
    return {
      showModal: true,
      partyName: '',
      area: '',
      ownerMobiles: [''],
      managerMobiles: [''],
      emails: [''],
      drugLicense: '',
      drugLicenseValidity: null,
      gstApplicable: false,
      gstNumber: '',
      expectedMonthlyTurnover: null,
      discount: null,
      creditType: null,
      creditDays: null,
      statementDuration: null,
      retailAppInstalled: false,
      transportType: null,
      busName: '',
      busTimings: '',
      status: null,
      drugLicenseImage: null,
      existingDrugLicenseImage: null,

      creditTypeOptions: [
        { value: 'cash', text: 'Cash' },
        { value: 'bill_to_bill', text: 'Bill to Bill' },
        { value: 'statement', text: 'Statement' },
      ],
      statementDurationOptions: [
        { value: 'weekly', text: 'Weekly' },
        { value: 'half_monthly', text: 'Half Monthly' },
        { value: 'monthly', text: 'Monthly' },
      ],
      transportTypeOptions: [
        { value: 'bus', text: 'Bus' },
        { value: 'courier', text: 'Courier' },
      ],
      statusOptions: [
        { value: 'pending', text: 'Pending' },
        { value: 'success', text: 'Success' },
        { value: 'failed', text: 'Failed' },
      ],

      saving: false,
      errorMessage: {},
    }
  },
  created() {
    if (this.editId) {
      this.fetchLeadDetail()
    }
  },
  methods: {
    onCreditTypeChange(value) {
      // Cash: clear both. Bill to Bill: clear duration. Statement: clear days. Null: clear both.
      if (value === 'cash' || !value) {
        this.creditDays = null
        this.statementDuration = null
      } else if (value === 'bill_to_bill') {
        this.statementDuration = null
      } else if (value === 'statement') {
        this.creditDays = null
      }
    },
    onTransportTypeChange(value) {
      // Only show bus name/timings when 'bus'. Otherwise clear.
      if (value !== 'bus') {
        this.busName = ''
        this.busTimings = ''
      }
    },
    addOwnerMobile() {
      this.ownerMobiles.push('')
    },
    removeOwnerMobile(index) {
      this.ownerMobiles.splice(index, 1)
      this.revalidateMobileErrors()
    },
    addManagerMobile() {
      this.managerMobiles.push('')
    },
    removeManagerMobile(index) {
      this.managerMobiles.splice(index, 1)
      this.revalidateMobileErrors()
    },
    addEmail() {
      this.emails.push('')
    },
    removeEmail(index) {
      this.emails.splice(index, 1)
    },

    showErrorToast(message) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: message || 'Please fix the highlighted errors',
          icon: 'AlertCircleIcon',
          variant: 'danger',
        },
      })
    },

    onMobileInput(field, index, value) {
      // Allow only digits and hyphen
      const cleaned = (value || '').replace(/[^\d-]/g, '')
      this.$set(this[field], index, cleaned)
      // Re-check errors live so they clear once input becomes valid
      this.revalidateMobileErrors()
    },

    isValidMobile(val) {
      const v = (val || '').trim()
      if (v === '') return true
      // Must contain at least one digit and only digits/hyphens
      return /^[\d-]+$/.test(v) && /\d/.test(v)
    },

    revalidateMobileErrors() {
      if (!this.errorMessage || typeof this.errorMessage !== 'object') return

      const ownerErrors = this.collectMobileErrors(this.ownerMobiles, 'Owner Mobile')
      const managerErrors = this.collectMobileErrors(this.managerMobiles, 'Manager Mobile')

      const updated = { ...this.errorMessage }

      if (ownerErrors.length === 0) {
        delete updated.owner_mobile
      } else if (updated.owner_mobile) {
        updated.owner_mobile = ownerErrors
      }

      if (managerErrors.length === 0) {
        delete updated.manager_mobile
      } else if (updated.manager_mobile) {
        updated.manager_mobile = managerErrors
      }

      this.errorMessage = updated
    },

    collectMobileErrors(mobiles, label) {
      const errors = []
      mobiles.forEach((m, idx) => {
        if (!this.isValidMobile(m)) {
          errors.push(`${label} #${idx + 1}: Only digits and hyphens are allowed`)
        }
      })
      return errors
    },

    formatArrayError(errorValue) {
      if (!errorValue) return ''
      if (Array.isArray(errorValue)) {
        const messages = []
        errorValue.forEach((item, idx) => {
          if (typeof item === 'string') {
            messages.push(item)
          } else if (typeof item === 'object' && item !== null) {
            Object.keys(item).forEach(k => {
              const val = Array.isArray(item[k]) ? item[k][0] : item[k]
              messages.push(`Item ${Number(k) + 1}: ${val}`)
            })
          } else {
            messages.push(`Item ${idx + 1}: ${item}`)
          }
        })
        return messages.join(' | ')
      }
      if (typeof errorValue === 'object') {
        const messages = []
        Object.keys(errorValue).forEach(k => {
          const val = Array.isArray(errorValue[k]) ? errorValue[k][0] : errorValue[k]
          messages.push(`Item ${Number(k) + 1}: ${val}`)
        })
        return messages.join(' | ')
      }
      return String(errorValue)
    },

    extractFirstErrorMessage(errObj) {
      if (!errObj) return 'Validation failed'
      if (typeof errObj === 'string') return errObj
      if (errObj.detail) return errObj.detail
      if (errObj.message) return errObj.message
      if (errObj.non_field_errors) {
        return Array.isArray(errObj.non_field_errors) ? errObj.non_field_errors[0] : errObj.non_field_errors
      }
      const keys = Object.keys(errObj)
      if (keys.length > 0) {
        const firstVal = errObj[keys[0]]
        if (Array.isArray(firstVal)) {
          return typeof firstVal[0] === 'string' ? firstVal[0] : `${keys[0]}: ${this.formatArrayError(firstVal)}`
        }
        if (typeof firstVal === 'string') return firstVal
        return `${keys[0]}: ${JSON.stringify(firstVal)}`
      }
      return 'Validation failed'
    },

    fetchLeadDetail() {
      axios.get(`/delivery/lead/${this.editId}/`)
        .then(res => {
          const { data } = res
          this.partyName = data.party_name || ''
          this.area = data.area || ''
          this.ownerMobiles = (data.owner_mobile && data.owner_mobile.length > 0) ? [...data.owner_mobile] : ['']
          this.managerMobiles = (data.manager_mobile && data.manager_mobile.length > 0) ? [...data.manager_mobile] : ['']
          this.emails = (data.emails && data.emails.length > 0) ? [...data.emails] : ['']
          this.drugLicense = data.drug_license || ''
          this.drugLicenseValidity = data.drug_license_validity || null
          this.gstApplicable = data.gst_applicable === true || data.gst_applicable === 'true'
          this.gstNumber = data.gst_number || ''
          this.expectedMonthlyTurnover = data.expected_monthly_turnover || null
          this.discount = data.discount || null
          this.creditType = data.credit_type || null
          this.creditDays = data.credit_days || null
          this.statementDuration = data.statement_duration || null
          this.retailAppInstalled = data.retail_app_installed === true || data.retail_app_installed === 'true'
          this.transportType = data.transport_type || null
          this.busName = data.bus_name || ''
          this.busTimings = data.bus_timings || ''
          this.status = data.status || null
          this.existingDrugLicenseImage = data.drug_license_image || null
        })
        .catch(error => {
          this.errorMessage = error?.response?.data || 'Failed to load lead details'
          this.showErrorToast(this.extractFirstErrorMessage(this.errorMessage))
        })
    },

    submitHandler(event) {
      event.preventDefault()

      this.errorMessage = {}

      // Required field validation
      const requiredErrors = {}
      if (!this.partyName || !this.partyName.trim()) {
        requiredErrors.party_name = ['Party Name is required']
      }
      if (!this.area || !this.area.trim()) {
        requiredErrors.area = ['Area is required']
      }
      if (Object.keys(requiredErrors).length > 0) {
        this.errorMessage = requiredErrors
        this.showErrorToast(this.extractFirstErrorMessage(requiredErrors))
        return
      }

      // Mobile validation
      const ownerErrors = this.collectMobileErrors(this.ownerMobiles, 'Owner Mobile')
      const managerErrors = this.collectMobileErrors(this.managerMobiles, 'Manager Mobile')

      if (ownerErrors.length || managerErrors.length) {
        this.errorMessage = {
          ...(ownerErrors.length && { owner_mobile: ownerErrors }),
          ...(managerErrors.length && { manager_mobile: managerErrors }),
        }
        this.showErrorToast(ownerErrors[0] || managerErrors[0])
        return
      }

      const formData = new FormData()
      formData.append('party_name', this.partyName)
      formData.append('area', this.area)

      const validOwnerMobiles = this.ownerMobiles.filter(m => m && m.trim() !== '')
      if (validOwnerMobiles.length > 0) {
        formData.append('owner_mobile', JSON.stringify(validOwnerMobiles))
      }

      const validManagerMobiles = this.managerMobiles.filter(m => m && m.trim() !== '')
      if (validManagerMobiles.length > 0) {
        formData.append('manager_mobile', JSON.stringify(validManagerMobiles))
      }

      const validEmails = this.emails.filter(e => e && e.trim() !== '')
      if (validEmails.length > 0) {
        formData.append('emails', JSON.stringify(validEmails))
      }

      if (this.drugLicense) {
        formData.append('drug_license', this.drugLicense)
      }
      if (this.drugLicenseValidity) {
        formData.append('drug_license_validity', this.drugLicenseValidity)
      }
      formData.append('gst_applicable', this.gstApplicable)
      if (this.gstNumber) {
        formData.append('gst_number', this.gstNumber)
      }
      if (this.expectedMonthlyTurnover) {
        formData.append('expected_monthly_turnover', this.expectedMonthlyTurnover)
      }
      if (this.discount !== null && this.discount !== '') {
        formData.append('discount', this.discount)
      }
      if (this.creditType) {
        formData.append('credit_type', this.creditType)
      }
      // Only send creditDays when bill_to_bill
      if (this.creditType === 'bill_to_bill' && this.creditDays) {
        formData.append('credit_days', this.creditDays)
      }
      // Only send statementDuration when statement
      if (this.creditType === 'statement' && this.statementDuration) {
        formData.append('statement_duration', this.statementDuration)
      }
      formData.append('retail_app_installed', this.retailAppInstalled)
      if (this.transportType) {
        formData.append('transport_type', this.transportType)
      }
      // Only send bus details when transport is bus
      if (this.transportType === 'bus' && this.busName) {
        formData.append('bus_name', this.busName)
      }
      if (this.transportType === 'bus' && this.busTimings) {
        formData.append('bus_timings', this.busTimings)
      }
      if (this.status) {
        formData.append('status', this.status)
      }
      if (this.drugLicenseImage) {
        formData.append('drug_license_image', this.drugLicenseImage)
      }

      this.saving = true

      const request = this.editId
        ? axios.patch(`/delivery/lead/${this.editId}/`, formData)
        : axios.post('/delivery/lead/', formData)

      request
        .then(res => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: res.data.message || (this.editId ? 'Lead Updated' : 'Lead Created'),
              icon: 'CheckIcon',
              variant: 'success',
            },
          })
          this.$emit('lead-saved')
          this.saving = false
        })
        .catch(error => {
          this.errorMessage = error?.response?.data || 'Something went wrong'
          this.showErrorToast(this.extractFirstErrorMessage(this.errorMessage))
          this.saving = false
        })
    },
  },
}
</script>

<style scoped>
.error-message {
  color: red;
}
</style>
